'use client';

/**
 * MarketingTrialExpiredOverlay
 *
 * Full-screen overlay shown inside the app when a marketing-only workspace's
 * trial has expired ($1 trial ended, no self-serve reactivation).
 *
 * Embeds the same calendar picker used on /book-demo so the user can book a
 * meeting to discuss a custom marketing plan.
 *
 * Only renders for marketing-only vendors (product_modules = ['marketing']).
 * Sales and "both" vendors retain the normal billing/reactivation flow.
 */

import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { Calendar, Clock, Mail, ChevronLeft, ChevronRight, X } from 'lucide-react';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useIsMarketingExpired } from '@/hooks/useSubscription';

interface TimeSlot {
  start: string;
  end: string;
}

const BOOK_DEMO_API = '/api/book-demo';

type Screen = 'CALENDAR' | 'BOOKING' | 'SUCCESS' | 'ERROR';

const MarketingTrialExpiredOverlay: FC = () => {
  const workspaceContext = useWorkspace();
  const activeWorkspace = workspaceContext?.activeWorkspace;

  const [dismissed, setDismissed] = useState(false);
  // Shared signal — also used by the no-subscription screen + billing offer so every
  // post-expiry surface agrees this is an expired marketing workspace (history-based;
  // survives setInactive() stripping 'marketing' from product_modules).
  const isTrialExpired = useIsMarketingExpired({ enabled: !dismissed });

  const [screen, setScreen] = useState<Screen>('CALENDAR');
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [bookingName, setBookingName] = useState('');
  const [bookingEmail, setBookingEmail] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [calendarUser, setCalendarUser] = useState<{ vendorId: number; userId: string } | null>(null);
  const [timeSlotsAuthId, setTimeSlotsAuthId] = useState<string | null>(null);

  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York', []);

  // Load calendar slots
  useEffect(() => {
    if (!isTrialExpired || dismissed) return;

    const loadCalendar = async () => {
      try {
        setSlotsLoading(true);
        const calRes = await fetch(`${BOOK_DEMO_API}/calendar`);
        const calData = await calRes.json();

        if (!calRes.ok || !calData.userData) {
          setScreen('ERROR');
          return;
        }

        const { vendorId, userId } = calData.userData;
        setCalendarUser({ vendorId, userId });

        const slotsRes = await fetch(
          `${BOOK_DEMO_API}/time-slots?vendorId=${vendorId}&userId=${userId}&timezone=${encodeURIComponent(userTimezone)}`,
        );
        const slotsData = await slotsRes.json();

        if (slotsRes.ok && slotsData.slots) {
          setSlots(slotsData.slots);
          setTimeSlotsAuthId(slotsData.authId);
        }
      } catch {
        setScreen('ERROR');
      } finally {
        setSlotsLoading(false);
      }
    };

    loadCalendar();
  }, [isTrialExpired, dismissed, userTimezone]);

  // Pre-fill name from workspace name if available
  useEffect(() => {
    if (activeWorkspace?.workspace_name) {
      setBookingName(activeWorkspace.workspace_name);
    }
  }, [activeWorkspace]);

  const getWeekDates = useCallback((offset: number) => {
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + offset * 7);
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(weekStart);
      day.setDate(day.getDate() + i);
      return day;
    });
  }, []);

  const weekDates = useMemo(() => getWeekDates(weekOffset), [getWeekDates, weekOffset]);
  const today = useMemo(() => new Date(), []);

  const filteredSlots = useMemo(() => {
    const dateStr = dayjs(selectedDate).format('YYYY-MM-DD');
    const nowLocal = dayjs();
    return slots.filter((slot) =>
      dayjs(slot.start).format('YYYY-MM-DD') === dateStr && dayjs(slot.start).isAfter(nowLocal),
    );
  }, [slots, selectedDate]);

  const handleBook = async () => {
    if (!selectedSlot || !bookingEmail.trim() || !timeSlotsAuthId) return;

    setBookingLoading(true);
    try {
      const slotDate = dayjs(selectedSlot.start);
      const res = await fetch(`${BOOK_DEMO_API}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: slotDate.format('YYYY-MM-DD'),
          time: slotDate.format('HH:mm'),
          email: bookingEmail.trim(),
          name: bookingName.trim(),
          timeZone: userTimezone,
          authId: timeSlotsAuthId,
          topic: `Marketing Plan Discussion — ${activeWorkspace?.workspace_name || bookingEmail}`,
          vendorId: calendarUser?.vendorId,
        }),
      });

      setBookingLoading(false);
      if (res.ok) {
        setScreen('SUCCESS');
      } else {
        setScreen('ERROR');
      }
    } catch {
      setBookingLoading(false);
      setScreen('ERROR');
    }
  };

  if (!isTrialExpired || dismissed) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Your trial has ended</h2>
                  <p className="text-sm text-gray-500">Your data is preserved — book a call to continue</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Value recap */}
          <div className="mt-4 p-4 bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl border border-violet-100">
            <p className="text-sm text-violet-800 font-medium">
              🎯 During your trial, MarketBetter captured leads, identified visitors, and monitored your AI search visibility.
              Let&apos;s discuss the right plan to keep it running.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {screen === 'SUCCESS' ? (
            <div className="flex flex-col items-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                <Mail className="text-green-600 w-7 h-7" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Meeting booked!</h3>
              <p className="text-gray-600 mt-2 max-w-sm">
                We&apos;ve sent a confirmation email. We&apos;ll discuss the best marketing plan for your needs.
              </p>
              <Button onClick={() => setDismissed(true)} className="mt-6">
                Close
              </Button>
            </div>
          ) : screen === 'ERROR' ? (
            <div className="flex flex-col items-center py-8 text-center">
              <p className="text-gray-600 mb-4">
                Unable to load the calendar. Please email us directly.
              </p>
              <a
                href="mailto:team@marketbetter.ai?subject=Marketing Plan Discussion"
                className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
              >
                <Mail className="w-4 h-4" /> Email team@marketbetter.ai
              </a>
            </div>
          ) : screen === 'BOOKING' && selectedSlot ? (
            <div className="space-y-4">
              <button
                onClick={() => setScreen('CALENDAR')}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
              >
                <ChevronLeft size={16} /> Back to calendar
              </button>
              <div className="text-center">
                <p className="font-medium text-lg">Confirm your meeting</p>
              </div>
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center gap-3">
                  <Clock className="text-gray-500 w-5 h-5" />
                  <div>
                    <p className="font-medium text-sm">{dayjs(selectedSlot.start).format('dddd, MMMM D, YYYY')}</p>
                    <p className="text-sm text-gray-600">
                      {dayjs(selectedSlot.start).format('h:mm A')} - {dayjs(selectedSlot.end).format('h:mm A')}{' '}
                      ({userTimezone})
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                  <input
                    type="text"
                    value={bookingName}
                    onChange={(e) => setBookingName(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={bookingEmail}
                    onChange={(e) => setBookingEmail(e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="you@company.com"
                  />
                </div>
              </div>
              <Button
                onClick={handleBook}
                disabled={bookingLoading || !bookingEmail.trim()}
                className="w-full bg-violet-600 hover:bg-violet-700"
              >
                {bookingLoading ? 'Booking...' : 'Confirm Meeting'}
              </Button>
            </div>
          ) : (
            /* CALENDAR SCREEN */
            <div className="space-y-6">
              {slotsLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm text-gray-500 mt-4">Loading available times...</p>
                </div>
              ) : (
                <>
                  <p className="font-medium text-center text-gray-900">Pick a time to discuss your marketing plan</p>

                  {/* Week navigation */}
                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))}
                      disabled={weekOffset === 0}
                    >
                      <ChevronLeft size={16} />
                    </Button>
                    <div className="flex-1 border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 border-b border-gray-200 flex items-center py-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                          <div key={day} className="flex-1 text-xs text-gray-500 text-center font-medium">
                            {day}
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center">
                        {weekDates.map((date, index) => {
                          const isPast = date < today && date.toDateString() !== today.toDateString();
                          const isSelected = date.toDateString() === selectedDate.toDateString();
                          return (
                            <div
                              key={index}
                              className={`flex-1 flex flex-col items-center py-3 cursor-pointer transition-all ${
                                isPast ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-50'
                              } ${isSelected ? 'bg-violet-600 text-white hover:bg-violet-500' : ''}`}
                              onClick={() => !isPast && setSelectedDate(date)}
                            >
                              <p className={`text-sm font-medium ${isPast ? 'text-gray-400' : ''}`}>{date.getDate()}</p>
                              <p className={`text-xs ${isPast ? 'text-gray-400' : 'text-gray-500'} ${isSelected ? 'text-white' : ''}`}>
                                {dayjs(date).format('MMM')}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setWeekOffset(Math.min(2, weekOffset + 1))}
                      disabled={weekOffset >= 2}
                    >
                      <ChevronRight size={16} />
                    </Button>
                  </div>

                  {/* Time slots */}
                  <div className="text-center">
                    <p className="text-sm text-gray-500">
                      30 min · {userTimezone}
                    </p>
                    {filteredSlots.length === 0 ? (
                      <p className="text-sm text-gray-400 mt-4">No available slots for this day</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 mt-4">
                        {filteredSlots.map((slot, i) => (
                          <button
                            key={i}
                            className="border border-gray-200 rounded-lg p-2.5 text-sm font-medium hover:bg-violet-50 hover:border-violet-300 transition-colors"
                            onClick={() => {
                              setSelectedSlot(slot);
                              setScreen('BOOKING');
                            }}
                          >
                            {dayjs(slot.start).format('h:mm A')}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingTrialExpiredOverlay;
